// =============================================================================
// iTravel 2.0 — Theme Store (Zustand)
// =============================================================================
// Manages the user's theme mode preference (light / dark / auto).
// Persisted to localStorage so the preference survives page reloads and
// app restarts. The 'auto' mode defers to the operating system's
// prefers-color-scheme media query at runtime.
// =============================================================================

import { create } from 'zustand';
import { combine, devtools, persist } from 'zustand/middleware';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** The three user-selectable theme modes */
export type ThemeMode = 'light' | 'dark' | 'auto';

export interface ThemeState {
  /** The user's chosen theme mode */
  mode: ThemeMode;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const STORAGE_KEY = 'itravel-theme-storage';

// -----------------------------------------------------------------------------
// Initial State
// -----------------------------------------------------------------------------

const initialState: ThemeState = {
  mode: 'auto',
};

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

export const useThemeStore = create(
  devtools(
    persist(
      combine(initialState, (set) => ({
        // -----------------------------------------------------------------
        // Actions
        // -----------------------------------------------------------------

        /**
         * Set the theme mode preference.
         * @param mode - 'light' | 'dark' | 'auto'
         */
        setMode: (mode: ThemeMode) => {
          set({ mode }, false, 'theme/setMode');
        },

        /**
         * Cycle through modes: auto → light → dark → auto …
         * Useful for a quick-toggle button.
         */
        cycleMode: () => {
          set(
            (state) => {
              const order: ThemeMode[] = ['auto', 'light', 'dark'];
              const currentIndex = order.indexOf(state.mode);
              const nextIndex = (currentIndex + 1) % order.length;
              return { mode: order[nextIndex] };
            },
            false,
            'theme/cycleMode',
          );
        },

        /**
         * Reset to the default theme mode (auto).
         */
        resetMode: () => {
          set({ mode: 'auto' }, false, 'theme/resetMode');
        },
      })),
      {
        name: STORAGE_KEY,
      },
    ),
    { name: 'ThemeStore', enabled: import.meta.env.DEV },
  ),
);

// -----------------------------------------------------------------------------
// Selectors (for optimized re-renders)
// -----------------------------------------------------------------------------

export const selectThemeMode = (state: ThemeState) => state.mode;
